import type { Message } from '../types';
import { chatCopy } from '../../config';
import { messageText } from './message-text';

/**
 * Build the standard export filename: chat-session-{id8}-{date}.{ext}
 * Shared by the client-side md/txt exports and the server-side .docx export (#268).
 */
export function buildExportFilename(sessionId: string, extension: string): string {
  // Defensive: ensure sessionId is valid for filename
  const safeSessionId = sessionId && sessionId.length >= 8
    ? sessionId.slice(0, 8)
    : 'unknown';
  return `chat-session-${safeSessionId}-${new Date().toISOString().slice(0, 10)}.${extension}`;
}

/**
 * Trigger a browser download of a Blob via a transient anchor element.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Download conversation as a file (Markdown or TXT format)
 */
export function downloadConversation(
  content: string,
  format: 'md' | 'txt',
  sessionId: string
): void {
  const mimeType = format === 'md' ? 'text/markdown' : 'text/plain';
  const blob = new Blob([content], { type: mimeType });
  triggerBlobDownload(blob, buildExportFilename(sessionId, format));
}

/**
 * The speaker label a turn carries in every export (#1731 phase 7, F-11).
 *
 * Replaces the flat `export.agentLabel`, which was a second source of truth
 * for a string the bubble and the print document already resolved through
 * `bubble.senderNames` — three formats, two tables, free to drift.
 *
 * It also carries the one thing none of them said: a spoken turn and a typed
 * one are indistinguishable once the audio is gone. MessageList marks the
 * speaker on screen for `metadata.source === 'voice'`; an export that dropped
 * the mark would read as though the family typed what they said aloud.
 */
export function exportSenderLabel(message: Message): string {
  const copy = chatCopy();
  const base =
    message.sender === 'user'
      ? copy.export.userLabel
      : copy.bubble.senderNames[message.sender] ?? copy.bubble.senderFallback;
  return message.metadata?.source === 'voice' ? `${base}${copy.export.spokenSuffix}` : base;
}

/**
 * Format Stevens V4 messages as text for export
 */
export function formatConversationAsText(
  messages: Message[],
  sessionId: string
): string {
  // #2109 R3-B24: labels and the locale arrive through the copy pack.
  const copy = chatCopy();
  const exportCopy = copy.export;
  const { labels } = exportCopy;
  // An empty `sessionId` label leaves the line out (the family pack does).
  const sessionLine = labels.sessionId
    ? `${labels.sessionId}: ${sessionId || exportCopy.unknownSession}\n`
    : '';
  const header = `${exportCopy.textHeaderTitle}
${exportCopy.textHeaderSubtitle}
${labels.generated}: ${new Date().toLocaleString(copy.locale)}
${labels.totalMessages}: ${messages.length}
${sessionLine}
===============================

`;

  const conversation = messages
    .filter(msg => !msg.isStreaming) // Exclude messages currently streaming
    .map(msg => {
      const timestamp = new Date(msg.timestamp).toLocaleString(copy.locale);
      const sender = exportSenderLabel(msg);

      // Clean export without system metadata
      return `[${timestamp}] ${sender}:
${messageText(msg)}
`;
    })
    .join('\n');

  return header + conversation;
}

/**
 * Format Stevens V4 messages as Markdown for export
 */
export function formatConversationAsMarkdown(
  messages: Message[],
  sessionId: string
): string {
  const copy = chatCopy();
  const exportDate = new Date().toLocaleString(copy.locale);
  const exportCopy = copy.export;
  const { labels } = exportCopy;

  // Build header section
  let markdown = `# ${exportCopy.markdownTitle}\n\n`;
  markdown += `**${labels.version}:** ${exportCopy.markdownVersionLabel}  \n`;
  if (labels.sessionId) markdown += `**${labels.sessionId}:** ${sessionId || exportCopy.unknownSession}  \n`;
  markdown += `**${labels.exported}:** ${exportDate}  \n`;
  markdown += `**${labels.totalMessages}:** ${messages.length}\n\n`;
  markdown += `---\n\n`;

  // Build conversation section
  const filteredMessages = messages.filter(msg => !msg.isStreaming);

  filteredMessages.forEach((msg, index) => {
    const timestamp = new Date(msg.timestamp).toLocaleString(copy.locale);
    const sender = exportSenderLabel(msg);

    markdown += `## ${exportCopy.messageHeading(index + 1)}\n\n`;
    markdown += `**${labels.from}:** ${sender}  \n`;
    markdown += `**${labels.time}:** ${timestamp}\n\n`;
    markdown += `${messageText(msg)}\n\n`;
    markdown += `---\n\n`;
  });

  return markdown;
}

/**
 * Format Stevens V3 messages as JSON for export
 * DEPRECATED: Removed from UI in favor of Markdown format
 */
// export function formatConversationAsJSON(
//   messages: StevensV3Message[],
//   sessionId: string
// ): string {
//   // Create a clean export format with relevant fields
//   const exportData = {
//     version: 'chat-v4-stevens-v3',
//     sessionId: sessionId || 'unknown',
//     exported: new Date().toISOString(),
//     messageCount: messages.length,
//     messages: messages.map(msg => ({
//       id: msg.id,
//       content: msg.content,
//       sender: msg.sender,
//       timestamp: msg.timestamp
//     }))
//   };
//
//   return JSON.stringify(exportData, null, 2);
// }
